import type {
  APIResponse,
  AccountDataExport,
  AccountExportClassPage,
  AccountExportFirstPage,
  AccountExportPagedClass,
  AccountExportSubjectReviewReference,
} from '@tabbio/types';
import { api } from './api';

/**
 * Settings > Privacy > "Download my data" (audit E10): the person's own
 * data from `GET /api/account/export`, merged into one JSON document.
 *
 * The first answer holds every class and a cursor for each class with
 * more; each cursor is followed with `?class=&cursor=` until it ends, and
 * the items are appended to that class in order. Any failed page fails the
 * whole export, so a partial file is never saved.
 */

export type AccountExportQuery = {
  class: AccountExportPagedClass;
  cursor: string;
};

export type AccountExportPageFetcher = (
  query: AccountExportQuery | null,
) => Promise<APIResponse<AccountExportFirstPage | AccountExportClassPage>>;

/** Pages one class may take: far beyond any real account, never endless. */
export const MAX_ACCOUNT_EXPORT_PAGES_PER_CLASS = 1000;

function unwrap<T>(response: APIResponse<T>): T {
  if (response.error || !response.data) {
    throw new Error(response.error?.message ?? 'Could not download your data');
  }
  return response.data;
}

export async function collectAccountExport(
  fetchPage: AccountExportPageFetcher,
  waitForRetry: (milliseconds: number) => Promise<void> = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds)),
): Promise<AccountDataExport> {
  const readPage = async (query: AccountExportQuery | null) => {
    for (let attempt = 0; ; attempt += 1) {
      const response = await fetchPage(query);
      if (response.error?.code !== 'RATE_LIMIT_EXCEEDED' || attempt >= 3) return unwrap(response);
      const retryAfter = (response.meta as {retryAfter?: unknown} | undefined)?.retryAfter;
      if (typeof retryAfter !== 'number' || !Number.isFinite(retryAfter) || retryAfter < 0 || retryAfter > 3600) return unwrap(response);
      await waitForRetry(Math.max(1000, Math.ceil(retryAfter * 1000)));
    }
  };
  const first = await readPage(null) as AccountExportFirstPage;
  const bundle = first.export;
  const classes = bundle as unknown as Record<string, unknown[]>;
  bundle.records ??= {};
  for (const [cls, start] of Object.entries(first.next ?? {})) {
    const seen = new Set<string>();
    let cursor: string | null = start ?? null;
    let pages = 0;
    while (cursor) {
      // A cursor that comes back again would loop forever.
      if (seen.has(cursor) || pages >= MAX_ACCOUNT_EXPORT_PAGES_PER_CLASS) {
        throw new Error('Could not download your data');
      }
      seen.add(cursor);
      pages += 1;
      const page = await readPage({ class: cls as AccountExportPagedClass, cursor }) as AccountExportClassPage;
      if (page.class !== cls || !Array.isArray(page.items)) throw new Error('Could not download your data');
      const recordModel = cls.startsWith('records.') ? cls.slice(8) : null;
      if (['__proto__','prototype','constructor'].includes(recordModel ?? cls)) throw new Error('Could not download your data');
      const target = recordModel ? bundle.records : classes;
      const field = recordModel ?? cls;
      if (!Array.isArray(target[field])) target[field] = [];
      target[field].push(...page.items);
      cursor = page.next ?? null;
    }
  }
  if (bundle.coverage?.subjectReview) {
    // Include references that arrived on continuation pages, not only previews.
    // A concurrent source change keeps the more restrictive observed result.
    const priority={approved:0,disabled:1,missing:2,review_required:3,known_other:4};
    const references=new Map<string,AccountExportSubjectReviewReference>();
    const appendReference = (reference: AccountExportSubjectReviewReference) => {
      const key=JSON.stringify([reference.sourceClass,reference.keys]);
      const prior=references.get(key);
      if (!prior || priority[reference.status]>priority[prior.status]) references.set(key,reference);
    };
    const rows=[...Object.values(bundle.records).flat(),...(bundle.cvs??[]),...(bundle.languageVariants??[]),...(bundle.applications??[]),...(bundle.talentMemberships??[]),
      ...(bundle.applications??[]).flatMap((row)=>row.receipts??[]),...(bundle.talentMemberships??[]).flatMap((row)=>row.receipts??[])];
    for (const row of rows) {
      const reference=(row as {subjectReview?:AccountExportSubjectReviewReference}|null)?.subjectReview;
      if (!reference) continue;
      appendReference(reference);
    }
    // Import source slots are reviewed independently. Only this server-created
    // metadata field is authoritative; never walk source or provider JSON.
    for (const value of bundle.records.CvImportEvidence ?? []) {
      if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
      const row = value as Record<string, unknown>;
      if (typeof row.id !== 'string' || !row.id || !Array.isArray(row.subjectReviews)) continue;
      if (row.subjectReviews.length > 2) throw new Error('Could not download your data');
      for (const value of row.subjectReviews) {
        if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
        const reference = value as Record<string, unknown>;
        if (reference.sourceClass !== 'records.CvImportEvidence' || !Array.isArray(reference.keys) || reference.keys.length !== 1) continue;
        const key = reference.keys[0];
        if (typeof key !== 'string' || key.length > 200 || ![`${row.id}_current`, `${row.id}_previous`].includes(key)) continue;
        if (typeof reference.status !== 'string' || !Object.hasOwn(priority, reference.status)) continue;
        appendReference(reference as unknown as AccountExportSubjectReviewReference);
      }
    }
    bundle.coverage.subjectReview.references=[...references.values()];
  }
  return bundle;
}

export function fetchAccountDataExport(): Promise<AccountDataExport> {
  return collectAccountExport((query) =>
    api.get<AccountExportFirstPage | AccountExportClassPage>(
      query
        ? `/api/account/export?class=${encodeURIComponent(query.class)}&cursor=${encodeURIComponent(query.cursor)}`
        : '/api/account/export',
    ),
  );
}

export function accountExportFileName(now: Date = new Date()): string {
  return `tabbio-data-${now.toISOString().slice(0, 10)}.json`;
}
